<script setup lang="ts">
const props = withDefaults(defineProps<{
  modelValue?: any
  items?: any[]
  itemTitle?: string
  itemValue?: string
  label?: string
  placeholder?: string
  variant?: 'outlined' | 'filled' | 'underlined' | 'solo'
  density?: 'default' | 'comfortable' | 'compact'
  hideDetails?: boolean | 'auto'
  disabled?: boolean
  readonly?: boolean
  clearable?: boolean
  multiple?: boolean
  chips?: boolean
  returnObject?: boolean
  prependIcon?: string
  appendIcon?: string
  errorMessages?: string | string[]
  rules?: ((v: any) => true | string)[]
  loading?: boolean
  menuProps?: Record<string, any>
  hint?: string
  persistentHint?: boolean
}>(), {
  variant: 'outlined',
  density: 'default',
  itemTitle: 'title',
  itemValue: 'value',
})

const emit = defineEmits<{
  'update:modelValue': [val: any]
  'click:clear': []
}>()

const open = ref(false)
const focused = ref(false)
const selectRef = ref<HTMLElement>()
const internalError = ref('')

const errorList = computed(() => {
  if (props.errorMessages) {
    return Array.isArray(props.errorMessages) ? props.errorMessages : [props.errorMessages]
  }
  if (internalError.value) return [internalError.value]
  return []
})
const hasError = computed(() => errorList.value.length > 0)

const normalizedItems = computed(() => {
  if (!props.items) return []
  return props.items.map(item => {
    if (typeof item === 'object' && item !== null) {
      return {
        title: item[props.itemTitle] ?? item.text ?? item.name ?? String(item),
        value: item[props.itemValue] ?? item.id ?? item,
        raw: item,
      }
    }
    return { title: String(item), value: item, raw: item }
  })
})

const displayValue = computed(() => {
  if (props.modelValue == null) return ''
  if (props.multiple && Array.isArray(props.modelValue)) {
    return props.modelValue.map(v => {
      const item = normalizedItems.value.find(i => i.value === v)
      return item?.title || String(v)
    })
  }
  const item = normalizedItems.value.find(i => i.value === props.modelValue)
  return item?.title || String(props.modelValue)
})

function selectItem(item: { title: string; value: any; raw: any }) {
  const val = props.returnObject ? item.raw : item.value
  if (props.multiple) {
    const current = Array.isArray(props.modelValue) ? [...props.modelValue] : []
    const idx = current.indexOf(val)
    if (idx >= 0) current.splice(idx, 1)
    else current.push(val)
    emit('update:modelValue', current)
  } else {
    emit('update:modelValue', val)
    open.value = false
  }
}

function isSelected(item: { value: any }) {
  if (props.multiple && Array.isArray(props.modelValue)) {
    return props.modelValue.includes(item.value)
  }
  return props.modelValue === item.value
}

function onClear() {
  emit('update:modelValue', props.multiple ? [] : null)
  emit('click:clear')
}

function validate() {
  if (!props.rules) return true
  for (const rule of props.rules) {
    const result = rule(props.modelValue)
    if (result !== true) { internalError.value = result; return false }
  }
  internalError.value = ''
  return true
}

function onClickOutside(e: MouseEvent) {
  if (selectRef.value && !selectRef.value.contains(e.target as Node)) {
    open.value = false
  }
}

const menuStyle = ref<Record<string, string>>({})

function updateMenuPosition() {
  if (!selectRef.value) return
  const rect = selectRef.value.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const openAbove = spaceBelow < 300 && rect.top > spaceBelow

  menuStyle.value = {
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    ...(openAbove
      ? { bottom: `${window.innerHeight - rect.top + 4}px` }
      : { top: `${rect.bottom + 4}px` }),
  }
}

watch(open, (val) => {
  if (val) nextTick(updateMenuPosition)
})

onMounted(() => document.addEventListener('click', onClickOutside))
onUnmounted(() => document.removeEventListener('click', onClickOutside))

defineExpose({ validate })
</script>

<template>
  <div
    ref="selectRef"
    class="m-select"
    :class="[
      `m-select--${variant}`,
      `m-select--${density}`,
      {
        'm-select--focused': focused || open,
        'm-select--error': hasError,
        'm-select--disabled': disabled,
        'm-select--open': open,
      }
    ]"
  >
    <div v-if="prependIcon" class="m-select__prepend">
      <MIcon :icon="prependIcon" />
    </div>

    <div class="m-select__control">
      <label v-if="label" class="m-select__label">{{ label }}</label>

      <div class="m-select__field" @click="!disabled && !readonly && (open = !open)">
        <div class="m-select__selection">
          <template v-if="chips && multiple && Array.isArray(displayValue)">
            <MChip v-for="text in displayValue" :key="text" size="small" closable @click:close="selectItem(normalizedItems.find(i => i.title === text)!)">
              {{ text }}
            </MChip>
          </template>
          <template v-else-if="Array.isArray(displayValue)">
            {{ displayValue.join(', ') }}
          </template>
          <template v-else>
            {{ displayValue }}
          </template>
          <span v-if="!displayValue || (Array.isArray(displayValue) && !displayValue.length)" class="m-select__placeholder">
            {{ placeholder }}
          </span>
        </div>

        <button v-if="clearable && modelValue != null && modelValue !== '' && !(Array.isArray(modelValue) && !modelValue.length)" class="m-select__clear" @click.stop="onClear">
          <MIcon icon="close" size="small" />
        </button>
        <MIcon :icon="appendIcon || 'chevron-down'" size="small" class="m-select__arrow" :class="{ 'm-select__arrow--open': open }" />
      </div>
    </div>

    <Teleport to="body">
      <div v-if="open" class="m-select__menu" :style="menuStyle">
        <div
          v-for="item in normalizedItems"
          :key="item.value"
          class="m-select__item"
          :class="{ 'm-select__item--selected': isSelected(item) }"
          @click="selectItem(item)"
        >
          <MIcon v-if="multiple" :icon="isSelected(item) ? 'check' : 'circle-outline'" size="small" class="m-select__check" />
          {{ item.title }}
        </div>
        <div v-if="!normalizedItems.length" class="m-select__empty">Keine Einträge</div>
      </div>
    </Teleport>

    <div v-if="hideDetails !== true" class="m-select__details">
      <span v-if="hasError" class="m-select__error-msg">{{ errorList[0] }}</span>
      <span v-else-if="hint && (persistentHint || focused)" class="m-select__hint">{{ hint }}</span>
    </div>
  </div>
</template>


<style scoped>
.m-select {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--m-space-2);
  width: 100%;
  position: relative;
}

.m-select__control { flex: 1; min-width: 0; }

.m-select__label {
  display: block;
  font-size: var(--m-font-size-sm);
  font-weight: 500;
  color: var(--m-on-surface-variant);
  margin-bottom: var(--m-space-1);
  transition: color var(--m-transition);
}
.m-select--focused .m-select__label { color: var(--m-primary); }
.m-select--error .m-select__label { color: var(--m-error); }

.m-select__field {
  display: flex;
  align-items: center;
  gap: var(--m-space-2);
  width: 100%;
  cursor: pointer;
  transition: all var(--m-transition);
}

.m-select--outlined .m-select__field {
  border: 1px solid var(--m-border);
  border-radius: var(--m-radius);
  background: var(--m-surface);
  padding: 0 var(--m-space-3);
}
.m-select--outlined.m-select--focused .m-select__field { border-color: var(--m-primary); box-shadow: 0 0 0 1px var(--m-primary); }
.m-select--outlined.m-select--error .m-select__field { border-color: var(--m-error); }

.m-select--filled .m-select__field {
  border: none;
  border-bottom: 1px solid var(--m-border);
  border-radius: var(--m-radius) var(--m-radius) 0 0;
  background: var(--m-surface-variant);
  padding: 0 var(--m-space-3);
}

.m-select--solo .m-select__field {
  border: none;
  border-radius: var(--m-radius);
  background: var(--m-surface);
  box-shadow: var(--m-shadow);
  padding: 0 var(--m-space-3);
}

.m-select__selection {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: var(--m-space-1);
  align-items: center;
  min-height: 40px;
  padding: var(--m-space-2) 0;
  font-size: var(--m-font-size-base);
  color: var(--m-on-surface);
}

.m-select--compact .m-select__selection { min-height: 32px; padding: var(--m-space-1) 0; font-size: var(--m-font-size-sm); }
.m-select--comfortable .m-select__selection { min-height: 36px; }

.m-select__placeholder { color: var(--m-on-surface-variant); opacity: 0.6; }

.m-select__clear,
.m-select__arrow {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--m-on-surface-variant);
  padding: 0;
  display: flex;
}
.m-select__arrow { transition: transform var(--m-transition); }
.m-select__arrow--open { transform: rotate(180deg); }

.m-select__menu {
  position: fixed;
  z-index: var(--m-z-dropdown);
  background: var(--m-surface);
  border: 1px solid var(--m-border);
  border-radius: var(--m-radius);
  box-shadow: var(--m-shadow-lg);
  max-height: 300px;
  overflow-y: auto;
  min-width: 200px;
  padding: var(--m-space-1) 0;
}

.m-select__item {
  display: flex;
  align-items: center;
  gap: var(--m-space-2);
  padding: var(--m-space-2) var(--m-space-4);
  cursor: pointer;
  font-size: var(--m-font-size-sm);
  transition: background var(--m-transition-fast);
}
.m-select__item:hover { background: var(--m-surface-variant); }
.m-select__item--selected { color: var(--m-primary); font-weight: 500; }

.m-select__empty {
  padding: var(--m-space-4);
  text-align: center;
  color: var(--m-on-surface-variant);
  font-size: var(--m-font-size-sm);
}

.m-select__details {
  width: 100%;
  padding: var(--m-space-1) var(--m-space-3) 0;
  font-size: var(--m-font-size-xs);
  min-height: 20px;
}
.m-select__error-msg { color: var(--m-error); }
.m-select__hint { color: var(--m-on-surface-variant); }

.m-select--disabled { opacity: 0.5; pointer-events: none; }
.m-select__prepend { margin-top: 28px; color: var(--m-on-surface-variant); }
</style>
